/********************************************************************************
 * COMPONENT: THEMES
 * ------------------------------------------------------------------------------
 * The themes component is designed to allow for the implementation of multiple
 * themes within the application. This component will be used load a select theme
 * based on a theme prop passed to it.
 *
 * This theme uses the Material UI Themeing Engine. Therefore, at least in the
 * beginning, this theme is going to wrap partner pages only. This will allow us
 * to quickly theme partner pages without having to worry about breaking the
 * rest of the application. It will also allow us to quickly implement a theme
 * that is well documented, supported by Google and officially supported by
 * the GatsbyJS development team.
 ********************************************************************************/

import React, { useMemo } from 'react';
import { createTheme, ThemeProvider, Theme, responsiveFontSizes } from '@mui/material/styles';

interface ThemeProps {
  theme?: any;
  children: React.ReactNode;
}

const Brand = ({ theme, children }: ThemeProps) => {
  // Construct the page theme values
  const pageTheme: any = {
    palette: {
      mode: 'light',
      ...theme?.palette,
    },
    typography: {
      ...theme?.typography,
      // Add custom stepdown for subtitle2
      subtitle2: {
        fontSize: '1rem', // Default value, will be overridden for xs
        '@media (max-width:600px)': {
          fontSize: '11px', // Custom stepdown for xs
        },
      },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: 50, // A large number for full pill effect
            padding: '8px 30px', // Custom padding: vertical | horizontal
            boxShadow: 'none', // Remove the default shadow
            '&:hover': {
              boxShadow: 'none', // Remove the shadow on hover
            },
          },
        },
      },
    },
  };

  /********************************************************************************
   * CREATE THEME
   * ------------------------------------------------------------------------------
   * The theme is created using the normal createTheme function from Material-UI.
   * However, ther is one important difference. The theme is created using the
   * useMemo hook.
   *
   * -------------------------------------------------------------------------------
   * * Memoized Theme Creation
   * The theme creation is memoized because it's being applied at a page by page
   * level. The theme is applied at a page by page level because it is the method
   * for supporting multiple themes within the application. This is a very important
   * feature for the application because it allows us to quickly theme partner pages
   * without having to worry about breaking the rest of the application.
   *
   * The challenge with this approach is that the theme is being created at the
   * page level. This means that the theme is being created every time the page
   * is rendered. This is not a problem for the application because the theme is
   * being created using the useMemo hook. This means that the theme is only created
   * when the theme prop changes. So...
   *
   * * DO NOT REMOVE THE useMemo HOOK
   ********************************************************************************/

  let theTheme = useMemo(
    () => {
      // create the theme
      let tempTheme = createTheme(pageTheme);
      // make the themes fonts responsive
      tempTheme = responsiveFontSizes(tempTheme);
      // return the theme
      return tempTheme;
    },
    // The theme is only created when the theme prop changes
    [theme],
  );

  // Wrap the children in the theme provider
  return <ThemeProvider theme={theTheme}>{children}</ThemeProvider>;
};

export default Brand;
